import { useState } from "react";
import type { WidgetDefinition } from "./widgets";

type DashboardCustomizerProps = {
  catalog: WidgetDefinition[];
  value: string[];
  saving: boolean;
  onSave: (ids: string[]) => unknown;
  onResetToDefault: () => unknown;
  onClose: () => void;
};

export function DashboardCustomizer({
  catalog,
  value,
  saving,
  onSave,
  onResetToDefault,
  onClose,
}: DashboardCustomizerProps) {
  const [selected, setSelected] = useState<string[]>(value);

  const unselected = catalog.filter((widget) => !selected.includes(widget.id));
  const displayOrder: WidgetDefinition[] = [
    ...selected
      .map((id) => catalog.find((widget) => widget.id === id))
      .filter((widget): widget is WidgetDefinition => Boolean(widget)),
    ...unselected,
  ];

  function toggle(id: string) {
    setSelected((prev) => (prev.includes(id) ? prev.filter((widgetId) => widgetId !== id) : [...prev, id]));
  }

  function move(id: string, direction: -1 | 1) {
    setSelected((prev) => {
      const index = prev.indexOf(id);
      const target = index + direction;
      if (index === -1 || target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  return (
    <article className="panel widget-customizer fade-in">
      <header className="panel-header">
        <h2>Customize Dashboard</h2>
        <span>{selected.length} pinned</span>
      </header>
      <p className="management-note">
        Pick which panels show up on your dashboard and use the arrows to arrange them.
      </p>
      <ul className="widget-customizer-list">
        {displayOrder.map((widget) => {
          const isSelected = selected.includes(widget.id);
          const position = selected.indexOf(widget.id);
          return (
            <li key={widget.id} className="widget-customizer-row">
              <label className="checkbox-field">
                <input type="checkbox" checked={isSelected} onChange={() => toggle(widget.id)} />
                {widget.label}
              </label>
              <div className="widget-reorder-btns">
                <button
                  type="button"
                  className="secondary-btn"
                  disabled={!isSelected || position === 0}
                  onClick={() => move(widget.id, -1)}
                  aria-label={`Move ${widget.label} up`}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="secondary-btn"
                  disabled={!isSelected || position === selected.length - 1}
                  onClick={() => move(widget.id, 1)}
                  aria-label={`Move ${widget.label} down`}
                >
                  ↓
                </button>
              </div>
            </li>
          );
        })}
      </ul>
      <div className="widget-customizer-actions">
        <button
          className="refresh-btn"
          type="button"
          disabled={saving}
          onClick={() => void onSave(selected)}
        >
          {saving ? "Saving..." : "Save"}
        </button>
        <button
          className="secondary-btn"
          type="button"
          disabled={saving}
          onClick={() => void onResetToDefault()}
        >
          Reset to default
        </button>
        <button className="secondary-btn" type="button" onClick={onClose}>
          Close
        </button>
      </div>
    </article>
  );
}
